import { useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { pipelinesApi } from "../api/pipelines";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { QueryBoundary } from "../components/QueryBoundary";
import { useToast } from "../components/toast/context";
import type { Pipeline } from "../types";

type GroupKey = "catchAll" | "lead" | "opportunity" | "uncategorized";

interface PipelineGroup {
  key: GroupKey;
  title: string;
  pipelines: Pipeline[];
}

const GROUP_ORDER: { key: GroupKey; title: string }[] = [
  { key: "catchAll", title: "Catch-all" },
  { key: "lead", title: "Lead pipelines" },
  { key: "opportunity", title: "Opportunities" },
  { key: "uncategorized", title: "Uncategorized" },
];

// Pure helper: split + sort pipelines into the four ordered groups. Within a
// group, sort by display_order (missing → last), tiebreak by name.
function groupPipelines(pipelines: Pipeline[]): PipelineGroup[] {
  const buckets: Record<GroupKey, Pipeline[]> = {
    catchAll: [],
    lead: [],
    opportunity: [],
    uncategorized: [],
  };

  for (const p of pipelines) {
    if (p.data.is_default) buckets.catchAll.push(p);
    else if (p.data.category === "lead") buckets.lead.push(p);
    else if (p.data.category === "opportunity") buckets.opportunity.push(p);
    else buckets.uncategorized.push(p);
  }

  const byOrderThenName = (a: Pipeline, b: Pipeline) => {
    const oa = a.data.display_order ?? Number.MAX_SAFE_INTEGER;
    const ob = b.data.display_order ?? Number.MAX_SAFE_INTEGER;
    if (oa !== ob) return oa - ob;
    return (a.data.name || "").localeCompare(b.data.name || "");
  };

  return GROUP_ORDER.map(({ key, title }) => ({
    key,
    title,
    pipelines: buckets[key].slice().sort(byOrderThenName),
  }));
}

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Uncategorized" },
  { value: "lead", label: "Lead" },
  { value: "opportunity", label: "Opportunity" },
];

interface RowProps {
  pipeline: Pipeline;
  onSetCategory: (id: string, value: string) => void;
}

// One sortable list row. The dnd-kit listeners live on a dedicated grip
// handle so the row <Link> and the <select> stay fully usable.
function SortablePipelineRow({ pipeline: p, onSetCategory }: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: p.id });
  const d = p.data;

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <li ref={setNodeRef} style={style}>
      <div className="flex items-center gap-3 px-6 py-4 hover:bg-surface-tint transition-colors">
        <button
          type="button"
          aria-label="Drag to reorder"
          className="shrink-0 cursor-grab text-ink-low/60 hover:text-ink transition-colors leading-none px-1"
          onClick={(e) => e.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          ⠿
        </button>
        <Link
          to={`/pipelines/${p.id}`}
          className="flex items-center gap-4 min-w-0 flex-1"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading font-semibold text-ink">{d.name}</span>
              <Pill value={d.status} />
              {d.is_default ? (
                <span className="pill pill--muted">catch-all</span>
              ) : null}
            </div>
            <div className="mt-0.5 text-xs text-ink-low truncate">
              {d.slug} · {d.stages?.length ?? 0} stages
              {d.goal ? ` · ${d.goal}` : ""}
            </div>
          </div>
          <div className="text-right text-sm text-ink-low shrink-0 space-y-0.5">
            <div>
              <span className="tnum font-medium text-ink">{d.stats?.active ?? 0}</span>{" "}
              active
            </div>
            <div>
              <span className="tnum font-medium text-ink">
                {d.stats?.goal_reached ?? 0}
              </span>{" "}
              goal reached
            </div>
            {d.stats?.parked ? (
              <div>
                <span className="tnum font-medium text-ink">{d.stats.parked}</span> parked
              </div>
            ) : null}
          </div>
        </Link>
        {d.is_default ? null : (
          <select
            className="input w-[150px] shrink-0"
            aria-label="Category"
            value={d.category ?? ""}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onChange={(e) => onSetCategory(p.id, e.target.value)}
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
      </div>
    </li>
  );
}

export function PipelinesPage() {
  const [status, setStatus] = useState("");
  const queryClient = useQueryClient();
  const toast = useToast();
  const queryKey = useMemo(() => ["pipelines", status] as const, [status]);

  const query = useQuery({
    queryKey,
    queryFn: () =>
      pipelinesApi.list({
        status: status || undefined,
        limit: 200,
      }),
  });

  const categoryMutation = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      pipelinesApi.setCategory(id, value === "" ? null : (value as "lead" | "opportunity")),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const groups = useMemo(
    () => groupPipelines(query.data ?? []),
    [query.data],
  );

  async function handleDragEnd(ev: DragEndEvent) {
    const { active, over } = ev;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // Both ids must belong to the same group for a reorder; cross-group
    // drags are a no-op (category is the selector's job).
    const group = groups.find(
      (g) =>
        g.pipelines.some((p) => p.id === activeId) &&
        g.pipelines.some((p) => p.id === overId),
    );
    if (!group) return;

    const ids = group.pipelines.map((p) => p.id);
    const fromIdx = ids.indexOf(activeId);
    const toIdx = ids.indexOf(overId);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

    const newGroupIds = arrayMove(ids, fromIdx, toIdx);

    // Optimistically reorder the cached list so the row jumps immediately.
    const prev = query.data ?? [];
    const orderIndex = new Map(newGroupIds.map((id, i) => [id, i]));
    const optimistic = prev.map((p) =>
      orderIndex.has(p.id)
        ? { ...p, data: { ...p.data, display_order: orderIndex.get(p.id)! } }
        : p,
    );
    queryClient.setQueryData(queryKey, optimistic);

    try {
      await pipelinesApi.reorder(newGroupIds);
    } catch (err) {
      toast.error(err);
    } finally {
      queryClient.invalidateQueries({ queryKey });
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="All pipelines"
        description="Each pipeline tracks contacts toward one goal. Open one for the stage board."
      />

      <div className="flex items-end gap-4 mb-5 pb-5 border-b border-rule">
        <div>
          <label className="label" htmlFor="pipeline-status-filter">
            Status
          </label>
          <select
            id="pipeline-status-filter"
            className="input w-[180px]"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="ml-auto text-sm text-ink-low pb-2">
          <span className="tnum font-medium text-ink">{query.data?.length ?? 0}</span>{" "}
          pipelines
        </div>
      </div>

      <QueryBoundary
        query={query}
        empty={
          <div className="empty-state">
            <div className="title">No pipelines match</div>
            <p className="hint">
              Pipelines are created via Jarvis (`pipeline_upsert`).
            </p>
          </div>
        }
        isEmpty={(p) => p.length === 0}
      >
        {() => (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="space-y-5">
              {groups
                .filter((g) => g.pipelines.length > 0)
                .map((group) => (
                  <section key={group.key} className="panel panel--flush">
                    <header className="column-header">
                      <div className="title">{group.title}</div>
                      <span className="count tnum">{group.pipelines.length}</span>
                    </header>
                    <SortableContext
                      items={group.pipelines.map((p) => p.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <ul
                        className="divide-y"
                        style={{ borderColor: "var(--c-rule)" }}
                      >
                        {group.pipelines.map((p) => (
                          <SortablePipelineRow
                            key={p.id}
                            pipeline={p}
                            onSetCategory={(id, value) =>
                              categoryMutation.mutate({ id, value })
                            }
                          />
                        ))}
                      </ul>
                    </SortableContext>
                  </section>
                ))}
            </div>
          </DndContext>
        )}
      </QueryBoundary>
    </div>
  );
}
